<script setup lang="ts">
import { cn } from '@ui/lib/utils.js';
import { cloneVNode, computed, Fragment, isVNode, useSlots, type VNode } from 'vue';

interface Props {
    stackOrder?: 'natural' | 'first-on-top';
}

const props = withDefaults(defineProps<Props>(), {
    stackOrder: 'natural',
});
const slots = useSlots();

function flattenVNodes(nodes: unknown): VNode[] {
    const out: VNode[] = [];

    const visit = (n: unknown) => {
        if (!n) return;

        if (Array.isArray(n)) {
            for (const item of n) visit(item);
            return;
        }

        if (!isVNode(n)) return;

        // Fragment: its children contains the real nodes
        if (n.type === Fragment) {
            visit(n.children);
            return;
        }

        // Filter out text and comment nodes; only keep element/component VNodes
        if (n.type !== 'comment' && typeof n.type !== 'symbol') {
            out.push(n);
        }
    };

    visit(nodes);
    return out;
}

const children = computed(() => {
    const vNodes: VNode[] = flattenVNodes(slots.default?.() ?? []);
    if (props.stackOrder === 'natural') {
        return vNodes;
    }

    const max = vNodes.length;
    return vNodes.map((vNode, index) =>
        cloneVNode(vNode, {
            style: {
                ...(vNode.props as any)?.style,
                position: 'relative',
                zIndex: max - index,
            },
        }),
    );
});
</script>

<template>
    <div
        data-slot="avatar-stack"
        :class="
            cn(
                'inline-flex',
                '[&_:not(:first-child)]:-ml-2',
                '**:data-[slot=avatar]:border-2',
                '**:data-[slot=avatar]:border-surface-background',
            )
        "
    >
        <template v-for="(child, i) in children" :key="i">
            <component :is="child" />
        </template>
    </div>
</template>

<style scoped></style>
